import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchSportsEvents, upvoteEvent, hasUserUpvoted } from '../services/api';
import { Event } from '../types';

const Events: React.FC = () => {
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const loadEvents = async () => {
            try {
                setLoading(true);
                const data = await fetchSportsEvents();
                setEvents(data);
            } catch (error) {
                console.error('Error loading events:', error);
            } finally {
                setLoading(false);
            }
        };

        loadEvents();
    }, []);

    const handleUpvote = (e: React.MouseEvent, eventId: string) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (hasUserUpvoted(eventId)) {
            alert('You have already upvoted this event!');
            return;
        }
        
        const newUpvotes = upvoteEvent(eventId);
        
        // Update the events list
        setEvents(prevEvents => {
            const updated = prevEvents.map(ev => 
                ev.id === eventId ? { ...ev, upvotes: newUpvotes } : ev
            );
            
            // Re-sort by upvotes
            return updated.sort((a, b) => {
                if (a.status === 'live' && b.status !== 'live') return -1;
                if (a.status !== 'live' && b.status === 'live') return 1;
                return (b.upvotes || 0) - (a.upvotes || 0);
            });
        });
    };

    if (loading) {
        return (
            <div style={{ padding: '40px 20px', textAlign: 'center', minHeight: '60vh' }}>
                <p style={{ fontSize: '1.2em' }}>Loading events...</p>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', backgroundColor: '#ecf0f1', minHeight: '100vh' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <h1 style={{ color: '#2c3e50' }}>📅 All Sports Events</h1>
                <p style={{ color: '#7f8c8d', fontSize: '1.1em' }}>
                    Browse all available sports events and start watching your favorites
                </p>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '20px',
                    marginTop: '30px'
                }}>
                    {events.map(event => (
                        <Link
                            key={event.id}
                            to={`/event/${event.id}`}
                            style={{ textDecoration: 'none' }}
                        >
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                padding: '20px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                height: '100%'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-5px)';
                                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50', flex: 1 }}>
                                        {event.name}
                                    </h3>
                                    <button
                                        onClick={(e) => handleUpvote(e, event.id)}
                                        style={{
                                            background: hasUserUpvoted(event.id) ? '#95a5a6' : '#e74c3c',
                                            border: 'none',
                                            borderRadius: '20px',
                                            padding: '5px 12px',
                                            color: 'white',
                                            cursor: hasUserUpvoted(event.id) ? 'not-allowed' : 'pointer',
                                            fontSize: '0.85em',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            marginLeft: '10px',
                                            flexShrink: 0
                                        }}
                                        disabled={hasUserUpvoted(event.id)}
                                    >
                                        👍 {event.upvotes || 0}
                                    </button>
                                </div>
                                <p style={{ margin: '5px 0', color: '#7f8c8d', fontSize: '0.9em' }}>
                                    📅 {event.date}
                                </p>
                                <p style={{ margin: '5px 0', color: '#7f8c8d', fontSize: '0.9em' }}>
                                    🕐 {event.time}
                                </p>
                                <p style={{ margin: '5px 0 15px 0', color: '#7f8c8d', fontSize: '0.9em' }}>
                                    📍 {event.location}
                                </p>
                                <button style={{
                                    width: '100%',
                                    padding: '10px',
                                    backgroundColor: '#3498db',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}>
                                    Watch →
                                </button>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Events;